/**
 * The planar wall graph, face extraction and room detection.
 *
 * The floor plan is authored as nodes plus wall centrelines (docs/02 §6). Every
 * piece of rendered geometry — room outlines, wall polygons, inner floor areas —
 * is DERIVED here and never stored.
 *
 * Two corrections from docs/02 §6, both proven by counterexample, are honoured:
 *
 *   (a) 2-CORE PRUNING DOES NOT REMOVE BRIDGES. Repeatedly deleting degree<=1
 *       vertices removes dangling filaments only. A bridge lies in NO cycle, so
 *       a degree-based prune can never remove it — two squares joined by one
 *       corridor wall has no vertex of degree <=1 at all, yet the outer face
 *       orbit still traverses the corridor twice. We therefore use TARJAN
 *       bridge-finding and strip bridge half-edges out of face orbits.
 *
 *   (b) SPURS DO NOT BREAK SHOELACE AREA. A spurred ring contributes exactly
 *       zero to the shoelace sum, so `signedArea` is exact on spurred rings.
 *       What a spur actually breaks is INWARD OFFSETTING — the miter solver
 *       sees a 180-degree reversal and divides by ~zero. `offsetRingInward`
 *       detects the reversal and skips the vertex rather than dividing.
 *
 * Everything in this module is a pure function of its arguments. No clock, no
 * randomness, no I/O, no mutation of inputs. Two offline devices given the same
 * plan compute byte-identical rooms with no coordination — which is why node
 * coincidence is resolved by QUANTISATION (order-independent) rather than by
 * pairwise epsilon comparison (order-dependent), and why every iteration order
 * in here is over sorted ids rather than over input arrays.
 */

import type { DetectedRoom, FloorPlan, Opening, PlanNode, Wall } from '../entities/plan.js'
import type { NodeId, RoomId, WallId } from '../ids.js'
import { asId } from '../ids.js'
import type { Mm, Vec2 } from '../units.js'
import { add, cross, dot, len, lenSq, mm, normalize, perp, scale, sub, vec } from '../units.js'

// ---------------------------------------------------------------------------
// Tolerances. All lengths are real-world millimetres.
// ---------------------------------------------------------------------------

/** Nodes whose coordinates quantise to the same cell are treated as one node. */
export const COINCIDENT_EPS_MM = 1e-6

/** Signed areas below this magnitude (mm^2) count as zero. */
export const AREA_EPS_MM2 = 1e-6

/** |cross(d1, d2)| below this, for unit d, means the directions are parallel. */
export const PARALLEL_EPS = 1e-9

/** Distance tolerance for "is this point on that segment" (mm). */
export const ON_SEGMENT_EPS_MM = 1e-9

/** A miter further than this multiple of the half-thickness is a spike. */
export const MITER_LIMIT = 16

/** An opening narrower than this after clamping is dropped rather than kept. */
export const MIN_OPENING_WIDTH_MM = 1

const cmpStr = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0)

// ---------------------------------------------------------------------------
// Ring geometry: shoelace area, centroid, winding, point-in-polygon
// ---------------------------------------------------------------------------

/**
 * Shoelace signed area of a closed ring (first vertex is NOT repeated last).
 * Positive = counter-clockwise in a y-up world, negative = clockwise.
 *
 * Exact on spurred rings: an out-and-back excursion contributes +k and -k to
 * the sum, which cancel exactly in binary floating point (docs/02 §6).
 */
export function signedArea(ring: readonly Vec2[]): number {
  const n = ring.length
  if (n < 3) return 0
  let sum = 0
  for (let i = 0; i < n; i++) {
    const p = ring[i]!
    const q = ring[(i + 1) % n]!
    sum += p.x * q.y - q.x * p.y
  }
  return sum / 2
}

/** Unsigned area of a ring. */
export function polygonArea(ring: readonly Vec2[]): number {
  return Math.abs(signedArea(ring))
}

export type Winding = 'ccw' | 'cw' | 'degenerate'

/** Winding of a ring, with a zero-area band that reports 'degenerate'. */
export function ringWinding(ring: readonly Vec2[]): Winding {
  const a = signedArea(ring)
  if (a > AREA_EPS_MM2) return 'ccw'
  if (a < -AREA_EPS_MM2) return 'cw'
  return 'degenerate'
}

/**
 * Area centroid of a ring. Falls back to the vertex average for a ring whose
 * area is zero (a pure spur, a collinear ring, a two-point ring), where the
 * area-weighted formula is 0/0.
 */
export function centroid(ring: readonly Vec2[]): Vec2 {
  const n = ring.length
  if (n === 0) return vec(0, 0)
  if (n === 1) return ring[0]!
  let a2 = 0
  let cx = 0
  let cy = 0
  for (let i = 0; i < n; i++) {
    const p = ring[i]!
    const q = ring[(i + 1) % n]!
    const c = p.x * q.y - q.x * p.y
    a2 += c
    cx += (p.x + q.x) * c
    cy += (p.y + q.y) * c
  }
  if (Math.abs(a2 / 2) <= AREA_EPS_MM2) {
    let sx = 0
    let sy = 0
    for (const p of ring) {
      sx += p.x
      sy += p.y
    }
    return vec(sx / n, sy / n)
  }
  return vec(cx / (3 * a2), cy / (3 * a2))
}

/** True when p lies on segment ab, within `eps` millimetres of it. */
export function pointOnSegment(
  p: Vec2,
  a: Vec2,
  b: Vec2,
  eps: number = ON_SEGMENT_EPS_MM,
): boolean {
  const ab = sub(b, a)
  const ap = sub(p, a)
  const l = len(ab)
  if (l <= eps) return len(ap) <= eps
  // |cross| / |ab| is the perpendicular distance from p to the infinite line.
  if (Math.abs(cross(ab, ap)) > eps * l) return false
  const t = dot(ap, ab) / l
  return t >= -eps && t <= l + eps
}

export type PointClass = 'inside' | 'outside' | 'boundary'

/**
 * Three-way point classification. Boundary is reported explicitly so callers
 * that need a strict interior (containment tests, representative points) are
 * never at the mercy of a ray that happens to graze a vertex.
 */
export function classifyPointInPolygon(
  p: Vec2,
  ring: readonly Vec2[],
  eps: number = ON_SEGMENT_EPS_MM,
): PointClass {
  const n = ring.length
  if (n === 0) return 'outside'
  for (let i = 0; i < n; i++) {
    if (pointOnSegment(p, ring[i]!, ring[(i + 1) % n]!, eps)) return 'boundary'
  }
  if (n < 3) return 'outside'
  // Crossing number with the half-open rule (yi > y) !== (yj > y): a vertex is
  // counted for exactly one of its two incident edges, so rays through
  // vertices resolve deterministically and identically on every device.
  let inside = false
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const a = ring[i]!
    const b = ring[j]!
    if (a.y > p.y !== b.y > p.y) {
      const xInt = a.x + ((p.y - a.y) / (b.y - a.y)) * (b.x - a.x)
      if (p.x < xInt) inside = !inside
    }
  }
  return inside ? 'inside' : 'outside'
}

/**
 * Ray-cast point-in-polygon. The polygon is CLOSED: a point exactly on an edge
 * or exactly on a vertex counts as inside. Callers needing the open set should
 * use `classifyPointInPolygon`.
 */
export function pointInPolygon(p: Vec2, ring: readonly Vec2[]): boolean {
  return classifyPointInPolygon(p, ring) !== 'outside'
}

function orient(a: Vec2, b: Vec2, c: Vec2): number {
  return (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x)
}

function sgn(v: number, eps: number): number {
  return v > eps ? 1 : v < -eps ? -1 : 0
}

/** True when segments ab and cd share at least one point (touching counts). */
export function segmentsIntersect(a: Vec2, b: Vec2, c: Vec2, d: Vec2): boolean {
  const mag = Math.max(1, Math.abs(a.x), Math.abs(a.y), Math.abs(b.x), Math.abs(b.y),
    Math.abs(c.x), Math.abs(c.y), Math.abs(d.x), Math.abs(d.y))
  const areaEps = ON_SEGMENT_EPS_MM * mag
  const s1 = sgn(orient(a, b, c), areaEps)
  const s2 = sgn(orient(a, b, d), areaEps)
  const s3 = sgn(orient(c, d, a), areaEps)
  const s4 = sgn(orient(c, d, b), areaEps)
  if (s1 * s2 < 0 && s3 * s4 < 0) return true
  if (s1 === 0 && pointOnSegment(c, a, b)) return true
  if (s2 === 0 && pointOnSegment(d, a, b)) return true
  if (s3 === 0 && pointOnSegment(a, c, d)) return true
  if (s4 === 0 && pointOnSegment(b, c, d)) return true
  return false
}

function nearlyEqual(a: Vec2, b: Vec2, eps: number = ON_SEGMENT_EPS_MM): boolean {
  return Math.abs(a.x - b.x) <= eps && Math.abs(a.y - b.y) <= eps
}

/**
 * True when the ring is a simple polygon: at least three distinct vertices, no
 * repeated vertex, no two non-adjacent edges touching, and adjacent edges
 * meeting only at their shared vertex.
 */
export function isSimplePolygon(ring: readonly Vec2[]): boolean {
  const n = ring.length
  if (n < 3) return false
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      if (nearlyEqual(ring[i]!, ring[j]!)) return false
    }
  }
  for (let i = 0; i < n; i++) {
    const a = ring[i]!
    const b = ring[(i + 1) % n]!
    for (let j = i + 1; j < n; j++) {
      const c = ring[j]!
      const d = ring[(j + 1) % n]!
      if (j === i + 1) {
        // Shared vertex is b === c. Overlap iff a far endpoint lies on the
        // other segment.
        if (pointOnSegment(d, a, b) || pointOnSegment(a, c, d)) return false
      } else if (i === 0 && j === n - 1) {
        // Shared vertex is a === d.
        if (pointOnSegment(c, a, b) || pointOnSegment(b, c, d)) return false
      } else if (segmentsIntersect(a, b, c, d)) {
        return false
      }
    }
  }
  return true
}

/**
 * A point strictly inside the region bounded by `outer` with `holes` removed,
 * found by a SCANLINE SWEEP: for every y strictly between two distinct vertex
 * heights, intersect the whole edge set with that horizontal line, sort the
 * crossings and take the midpoint of the widest interior span.
 *
 * This is the tier that never fails when an interior exists at all — unlike the
 * area centroid (outside for an L, inside the void for an annulus) and unlike
 * nudged edge midpoints (which need a known winding and a small enough nudge).
 * Scanning y ascending and spans left to right, and improving only on a STRICT
 * increase in width, makes the choice a pure function of the geometry.
 *
 * Returns null when the region has no interior (a degenerate or empty ring).
 */
function scanlineInteriorPoint(
  outer: readonly Vec2[],
  holes: readonly (readonly Vec2[])[],
): Vec2 | null {
  if (outer.length < 3) return null
  const edges: [Vec2, Vec2][] = []
  const ys: number[] = []
  const pushRing = (r: readonly Vec2[]): void => {
    for (let i = 0; i < r.length; i++) {
      edges.push([r[i]!, r[(i + 1) % r.length]!])
      ys.push(r[i]!.y)
    }
  }
  pushRing(outer)
  for (const h of holes) if (h.length >= 3) pushRing(h)

  const levels = [...new Set(ys)].sort((a, b) => a - b)
  if (levels.length < 2) return null

  let best: Vec2 | null = null
  let bestWidth = 0
  for (let k = 0; k + 1 < levels.length; k++) {
    const y = (levels[k]! + levels[k + 1]!) / 2
    // No vertex can sit exactly on y, so the half-open crossing rule below is
    // never asked to resolve a graze.
    const xs: number[] = []
    for (const [a, b] of edges) {
      if (a.y > y === b.y > y) continue
      xs.push(a.x + ((y - a.y) / (b.y - a.y)) * (b.x - a.x))
    }
    if (xs.length < 2) continue
    xs.sort((p, q) => p - q)
    for (let i = 0; i + 1 < xs.length; i += 2) {
      const width = xs[i + 1]! - xs[i]!
      if (width > bestWidth) {
        bestWidth = width
        best = vec((xs[i]! + xs[i + 1]!) / 2, y)
      }
    }
  }
  return bestWidth > 0 ? best : null
}

/**
 * A point strictly inside a simple ring. The centroid works for convex rooms;
 * for L-shaped and concave rooms it can land outside, so we fall back to short
 * diagonals, then to edge midpoints nudged along the inward normal, and finally
 * to the scanline sweep, which cannot fail on a ring with a non-empty interior.
 *
 * The nudge tier expects a CCW ring (the inward normal is `perp` of the edge
 * direction); the centroid, diagonal and scanline tiers are winding-agnostic,
 * so a CW ring is still handled correctly.
 */
export function representativeInteriorPoint(ring: readonly Vec2[]): Vec2 {
  const n = ring.length
  if (n === 0) return vec(0, 0)
  if (n < 3) return centroid(ring)
  const c = centroid(ring)
  if (classifyPointInPolygon(c, ring) === 'inside') return c
  for (let i = 0; i < n; i++) {
    const a = ring[i]!
    const b = ring[(i + 2) % n]!
    const m = vec((a.x + b.x) / 2, (a.y + b.y) / 2)
    if (classifyPointInPolygon(m, ring) === 'inside') return m
  }
  for (let i = 0; i < n; i++) {
    const a = ring[i]!
    const b = ring[(i + 1) % n]!
    const d = normalize(sub(b, a))
    if (d.x === 0 && d.y === 0) continue
    const inward = perp(d)
    const midx = (a.x + b.x) / 2
    const midy = (a.y + b.y) / 2
    for (const k of [1e-3, 1e-2, 1e-1, 1, 10]) {
      const q = vec(midx + inward.x * k, midy + inward.y * k)
      if (classifyPointInPolygon(q, ring) === 'inside') return q
    }
  }
  return scanlineInteriorPoint(ring, []) ?? c
}

/** Area centroid of `outer` with the areas of `holes` subtracted. */
function areaCentroidWithHoles(
  outer: readonly Vec2[],
  holes: readonly (readonly Vec2[])[],
): Vec2 {
  const ao = Math.abs(signedArea(outer))
  if (ao <= AREA_EPS_MM2) return centroid(outer)
  const co = centroid(outer)
  let wa = ao
  let wx = co.x * ao
  let wy = co.y * ao
  for (const h of holes) {
    const ah = Math.abs(signedArea(h))
    if (ah <= AREA_EPS_MM2) continue
    const ch = centroid(h)
    wa -= ah
    wx -= ch.x * ah
    wy -= ch.y * ah
  }
  if (Math.abs(wa) <= AREA_EPS_MM2) return co
  return vec(wx / wa, wy / wa)
}

/**
 * An anchor point for a room: guaranteed strictly inside `outer` and strictly
 * outside every hole, so a room label, an occupancy tint or a sticky-note pin
 * dropped here always lands on the room's own floor.
 *
 * The hole-aware area centroid is used whenever it qualifies — it is the point
 * a human would pick and it keeps convex rooms exactly centred — but it is only
 * a candidate, not the answer. For an L-shaped room it lies in the missing
 * corner; for a ring-shaped room it lies in the void. Either way the scanline
 * sweep takes over.
 */
export function interiorPointOf(
  outer: readonly Vec2[],
  holes: readonly (readonly Vec2[])[] = [],
): Vec2 {
  const c = areaCentroidWithHoles(outer, holes)
  if (
    classifyPointInPolygon(c, outer) === 'inside' &&
    holes.every(h => classifyPointInPolygon(c, h) === 'outside')
  ) {
    return c
  }
  return scanlineInteriorPoint(outer, holes) ?? c
}

// ---------------------------------------------------------------------------
// Inward offset (the miter solver)
// ---------------------------------------------------------------------------

/**
 * Offset every edge of the ring to its LEFT by the corresponding half
 * thickness, then miter adjacent offset lines together.
 *
 * "Left" makes the function winding-agnostic and does the right thing for both
 * uses: on a CCW room ring the interior is on the left, so the ring shrinks to
 * the inner face of the walls; on a CW hole ring the left is *away* from the
 * enclosed region, so the hole grows — which is exactly right, because the
 * wall around a hole eats into the room from that side too.
 *
 * Spur handling (docs/02 §6 correction (b)): where two consecutive edges run in
 * opposite directions the two offset lines are parallel and the miter is at
 * infinity. We SKIP the vertex instead of dividing by ~zero.
 *
 * Guards, any of which returns the ORIGINAL ring rather than garbage: a
 * thickness array that does not describe every edge, an edge that reversed
 * direction (the offset exceeded the local feature size and the polygon turned
 * inside out), a result that is not a simple polygon, a signed area that did
 * not strictly decrease (an inward offset must shrink a CCW ring and must grow
 * the region enclosed by a CW ring), or a miter longer than MITER_LIMIT
 * half-thicknesses.
 */
export function offsetRingInwardVariable(
  ring: readonly Vec2[],
  halfThicknessPerEdge: readonly number[],
): Vec2[] {
  const n = ring.length
  const original = ring.map(p => vec(p.x, p.y))
  if (n < 3) return original
  // One half-thickness per EDGE, no more and no fewer. Silently padding a short
  // array with zeros would offset part of a room and leave the rest on the
  // centreline, which reads as a real polygon and is wrong everywhere.
  if (halfThicknessPerEdge.length !== n) return original
  const anyPositive = halfThicknessPerEdge.some(h => (h ?? 0) > 0)
  if (!anyPositive) return original

  const dir: Vec2[] = []
  const nrm: Vec2[] = []
  const half: number[] = []
  for (let i = 0; i < n; i++) {
    const a = ring[i]!
    const b = ring[(i + 1) % n]!
    const d = normalize(sub(b, a))
    if (d.x === 0 && d.y === 0) return original // zero-length edge: nothing sane to do
    dir.push(d)
    nrm.push(perp(d))
    half.push(Math.max(0, halfThicknessPerEdge[i] ?? 0))
  }

  const out: Vec2[] = []
  /** For each output vertex, the source edge that starts at it. */
  const srcEdge: number[] = []
  let worstMiter = 0
  for (let i = 0; i < n; i++) {
    const prev = (i - 1 + n) % n
    const v = ring[i]!
    const d1 = dir[prev]!
    const d2 = dir[i]!
    const n1 = nrm[prev]!
    const n2 = nrm[i]!
    const h1 = half[prev]!
    const h2 = half[i]!
    const det = cross(d1, d2)
    if (Math.abs(det) < PARALLEL_EPS) {
      if (dot(d1, d2) < 0) {
        // 180-degree reversal: the tip of a spur. Skip it — do not divide.
        continue
      }
      // Straight-through vertex. Use the thicker of the two walls so the room
      // is never overstated.
      const h = Math.max(h1, h2)
      out.push(vec(v.x + n2.x * h, v.y + n2.y * h))
      srcEdge.push(i)
      worstMiter = Math.max(worstMiter, h === 0 ? 0 : 1)
      continue
    }
    const c1 = dot(n1, v) + h1
    const c2 = dot(n2, v) + h2
    const x = (c1 * n2.y - n1.y * c2) / det
    const y = (n1.x * c2 - c1 * n2.x) / det
    const p = vec(x, y)
    const h = Math.max(h1, h2)
    if (h > 0) worstMiter = Math.max(worstMiter, len(sub(p, v)) / h)
    out.push(p)
    srcEdge.push(i)
  }

  if (worstMiter > MITER_LIMIT) return original
  if (out.length < 3) return original

  // EDGE-COLLAPSE GUARD. This is the check that actually catches an offset
  // larger than the local feature size, and neither winding nor area nor
  // simplicity catches it: offset a 1000 mm square inward by 600 and you get a
  // 200 mm square that is still simple, still counter-clockwise, and still
  // smaller — but every edge now runs BACKWARDS, because the polygon turned
  // inside out. Requiring each surviving edge to keep pointing the same way as
  // the edge it came from rejects exactly that.
  const m = out.length
  for (let k = 0; k < m; k++) {
    const i = srcEdge[k]!
    const j = srcEdge[(k + 1) % m]!
    // Vertices were skipped between these two, so this output edge does not
    // correspond to a single source edge and has no direction to preserve.
    if (j !== (i + 1) % n) continue
    if (dot(sub(out[(k + 1) % m]!, out[k]!), dir[i]!) <= 0) return original
  }

  const a0 = signedArea(original)
  const a1 = signedArea(out)
  if (Math.abs(a0) <= AREA_EPS_MM2) return original
  if (Math.abs(a1) <= AREA_EPS_MM2) return original
  if (Math.sign(a1) !== Math.sign(a0)) return original
  if (!(a1 < a0)) return original
  if (!isSimplePolygon(out)) return original
  return out
}

/** Constant-thickness form of {@link offsetRingInwardVariable}. */
export function offsetRingInward(ring: readonly Vec2[], halfThickness: number): Vec2[] {
  if (!(halfThickness > 0)) return ring.map(p => vec(p.x, p.y))
  return offsetRingInwardVariable(ring, ring.map(() => halfThickness))
}

// ---------------------------------------------------------------------------
// The graph
// ---------------------------------------------------------------------------

export type DropReason = 'missing-node' | 'zero-length' | 'duplicate-wall' | 'duplicate-id'

export interface DroppedWall {
  readonly wallId: WallId
  readonly reason: DropReason
}

export interface HalfEdge {
  /** Index into `WallGraph.halfEdges`. */
  readonly index: number
  readonly wallId: WallId
  /** Canonical node this half-edge leaves. */
  readonly from: NodeId
  /** Canonical node this half-edge enters. */
  readonly to: NodeId
  /** Index of the opposite half-edge. */
  readonly twin: number
  /** atan2 of the direction of travel, in (-PI, PI]. */
  readonly angle: number
  /** Position of this half-edge inside `from`'s angle-sorted `outgoing`. */
  readonly slot: number
}

export interface GraphNode {
  readonly id: NodeId
  readonly p: Vec2
  /** All plan node ids that collapsed onto this one, sorted; includes `id`. */
  readonly merged: readonly NodeId[]
  /** Outgoing half-edge indices, sorted by angle ascending (CCW order). */
  readonly outgoing: readonly number[]
}

export interface WallGraph {
  readonly nodes: ReadonlyMap<NodeId, GraphNode>
  /** Canonical node ids, ascending. Iterate this, never a Map's insertion order. */
  readonly nodeIds: readonly NodeId[]
  readonly halfEdges: readonly HalfEdge[]
  /** Surviving walls, with endpoints rewritten to canonical node ids. */
  readonly walls: ReadonlyMap<WallId, Wall>
  /** Surviving wall ids, ascending. */
  readonly wallIds: readonly WallId[]
  /** wallId -> [half-edge a->b, half-edge b->a]. */
  readonly wallEdges: ReadonlyMap<WallId, readonly [number, number]>
  /** Plan node id -> canonical node id after coincidence merging. */
  readonly canonical: ReadonlyMap<NodeId, NodeId>
  /** Walls that lie in no cycle (Tarjan). Never part of a room boundary. */
  readonly bridges: ReadonlySet<WallId>
  /** Walls the build rejected, with why. */
  readonly dropped: readonly DroppedWall[]
}

export interface BuildGraphOptions {
  /**
   * Nodes are snapped to a grid of this size (mm) before being compared, so
   * coincidence resolution is order-independent. Default COINCIDENT_EPS_MM.
   */
  mergeToleranceMm?: number
}

interface MutableHalfEdge {
  index: number
  wallId: WallId
  from: NodeId
  to: NodeId
  twin: number
  angle: number
  slot: number
}

/**
 * Build the planar wall graph.
 *
 * Defensive about authored data: nodes with duplicate ids collapse, coincident
 * nodes merge, walls referencing missing nodes are dropped, zero-length walls
 * (including walls whose two endpoints merged) are dropped, and a second wall
 * between the same pair of nodes is dropped. Everything dropped is reported on
 * `graph.dropped` rather than thrown, because a half-drawn plan is a normal
 * intermediate state in an editor.
 */
export function buildGraph(plan: FloorPlan, opts?: BuildGraphOptions): WallGraph {
  const tol = opts?.mergeToleranceMm ?? COINCIDENT_EPS_MM
  const grid = tol > 0 ? tol : COINCIDENT_EPS_MM

  // Duplicate node ids: keep the lexicographically smallest position so the
  // choice does not depend on array order.
  const byId = new Map<NodeId, PlanNode>()
  for (const n of plan.nodes) {
    const prev = byId.get(n.id)
    if (prev === undefined) {
      byId.set(n.id, n)
    } else if (n.p.x < prev.p.x || (n.p.x === prev.p.x && n.p.y < prev.p.y)) {
      byId.set(n.id, n)
    }
  }

  // Coincidence by quantisation: order-independent, unlike pairwise epsilon.
  const byCell = new Map<string, PlanNode[]>()
  for (const n of byId.values()) {
    const key = `${Math.round(n.p.x / grid)}|${Math.round(n.p.y / grid)}`
    const bucket = byCell.get(key)
    if (bucket) bucket.push(n)
    else byCell.set(key, [n])
  }

  const canonical = new Map<NodeId, NodeId>()
  const repOf = new Map<NodeId, { id: NodeId; p: Vec2; merged: NodeId[] }>()
  for (const bucket of byCell.values()) {
    const sorted = [...bucket].sort((a, b) => cmpStr(a.id, b.id))
    const rep = sorted[0]!
    for (const n of sorted) canonical.set(n.id, rep.id)
    repOf.set(rep.id, { id: rep.id, p: rep.p, merged: sorted.map(n => n.id) })
  }

  const dropped: DroppedWall[] = []
  const walls = new Map<WallId, Wall>()
  const seenPair = new Map<string, WallId>()
  const sortedWalls = [...plan.walls].sort((a, b) => cmpStr(a.id, b.id))
  for (const w of sortedWalls) {
    if (walls.has(w.id)) {
      dropped.push({ wallId: w.id, reason: 'duplicate-id' })
      continue
    }
    const a = canonical.get(w.a)
    const b = canonical.get(w.b)
    if (a === undefined || b === undefined) {
      dropped.push({ wallId: w.id, reason: 'missing-node' })
      continue
    }
    if (a === b) {
      dropped.push({ wallId: w.id, reason: 'zero-length' })
      continue
    }
    const key = a < b ? `${a}|${b}` : `${b}|${a}`
    if (seenPair.has(key)) {
      dropped.push({ wallId: w.id, reason: 'duplicate-wall' })
      continue
    }
    seenPair.set(key, w.id)
    walls.set(w.id, { ...w, a, b })
  }

  const halfEdges: MutableHalfEdge[] = []
  const wallEdges = new Map<WallId, readonly [number, number]>()
  const outgoing = new Map<NodeId, number[]>()
  for (const id of repOf.keys()) outgoing.set(id, [])
  const wallIds = [...walls.keys()].sort(cmpStr)
  for (const wid of wallIds) {
    const w = walls.get(wid)!
    const pa = repOf.get(w.a)!.p
    const pb = repOf.get(w.b)!.p
    const i = halfEdges.length
    halfEdges.push({
      index: i, wallId: wid, from: w.a, to: w.b, twin: i + 1,
      angle: Math.atan2(pb.y - pa.y, pb.x - pa.x), slot: -1,
    })
    halfEdges.push({
      index: i + 1, wallId: wid, from: w.b, to: w.a, twin: i,
      angle: Math.atan2(pa.y - pb.y, pa.x - pb.x), slot: -1,
    })
    wallEdges.set(wid, [i, i + 1])
    outgoing.get(w.a)!.push(i)
    outgoing.get(w.b)!.push(i + 1)
  }

  const nodeIds = [...repOf.keys()].sort(cmpStr)
  const nodes = new Map<NodeId, GraphNode>()
  for (const id of nodeIds) {
    const rep = repOf.get(id)!
    const out = outgoing.get(id)!
    out.sort((x, y) => {
      const hx = halfEdges[x]!
      const hy = halfEdges[y]!
      if (hx.angle !== hy.angle) return hx.angle - hy.angle
      // Exactly collinear incident walls: order by wall id so the embedding is
      // still deterministic.
      return cmpStr(hx.wallId, hy.wallId)
    })
    for (let s = 0; s < out.length; s++) halfEdges[out[s]!]!.slot = s
    nodes.set(id, { id, p: rep.p, merged: rep.merged, outgoing: out })
  }

  const bridges = findBridges(nodeIds, walls)

  return { nodes, nodeIds, halfEdges, walls, wallIds, wallEdges, canonical, bridges, dropped }
}

/** Degree of a node in the built graph (0 for unknown or isolated nodes). */
export function nodeDegree(graph: WallGraph, nodeId: NodeId): number {
  const canonicalId = graph.canonical.get(nodeId) ?? nodeId
  return graph.nodes.get(canonicalId)?.outgoing.length ?? 0
}

/**
 * Tarjan bridge-finding, iterative so a long corridor cannot blow the stack.
 *
 * This is the thing 2-core pruning cannot do (docs/02 §6): a bridge lies in no
 * cycle, so no degree-based prune ever removes it.
 */
export function findBridges(
  nodeIds: readonly NodeId[],
  walls: ReadonlyMap<WallId, Wall>,
): Set<WallId> {
  const adj = new Map<NodeId, { wall: WallId; to: NodeId }[]>()
  for (const id of nodeIds) adj.set(id, [])
  const sortedWallIds = [...walls.keys()].sort(cmpStr)
  for (const wid of sortedWallIds) {
    const w = walls.get(wid)!
    adj.get(w.a)?.push({ wall: wid, to: w.b })
    adj.get(w.b)?.push({ wall: wid, to: w.a })
  }

  const bridges = new Set<WallId>()
  const disc = new Map<NodeId, number>()
  const low = new Map<NodeId, number>()
  let timer = 0

  interface Frame { node: NodeId; parentWall: WallId | null; i: number }

  for (const root of nodeIds) {
    if (disc.has(root)) continue
    disc.set(root, timer)
    low.set(root, timer)
    timer++
    const stack: Frame[] = [{ node: root, parentWall: null, i: 0 }]
    while (stack.length > 0) {
      const frame = stack[stack.length - 1]!
      const edges = adj.get(frame.node)!
      if (frame.i < edges.length) {
        const e = edges[frame.i]!
        frame.i++
        if (e.wall === frame.parentWall) continue
        const seen = disc.get(e.to)
        if (seen !== undefined) {
          low.set(frame.node, Math.min(low.get(frame.node)!, seen))
        } else {
          disc.set(e.to, timer)
          low.set(e.to, timer)
          timer++
          stack.push({ node: e.to, parentWall: e.wall, i: 0 })
        }
      } else {
        stack.pop()
        const parent = stack[stack.length - 1]
        if (parent !== undefined) {
          low.set(parent.node, Math.min(low.get(parent.node)!, low.get(frame.node)!))
          if (low.get(frame.node)! > disc.get(parent.node)! && frame.parentWall !== null) {
            bridges.add(frame.parentWall)
          }
        }
      }
    }
  }
  return bridges
}

/**
 * The next half-edge of the face to the LEFT of `hIndex`.
 *
 * Algorithm: the classic HALF-EDGE FACE-ORBIT TRAVERSAL of a planar embedding
 * (also called the "next-edge-clockwise" or minimal-cycle walk). From a
 * half-edge u->v, take its twin v->u and rotate CLOCKWISE around v to the next
 * incident edge — i.e. the immediate predecessor in v's CCW angle order. This
 * traverses every bounded face counter-clockwise (positive signed area) and
 * every unbounded face clockwise (negative signed area), which is what makes
 * "outer face == negative area" a reliable test rather than a heuristic.
 */
export function nextHalfEdge(graph: WallGraph, hIndex: number): number {
  const h = graph.halfEdges[hIndex]!
  const t = graph.halfEdges[h.twin]!
  const node = graph.nodes.get(t.from)!
  const deg = node.outgoing.length
  return node.outgoing[(t.slot - 1 + deg) % deg]!
}

export interface FaceOrbit {
  /** Half-edge indices in traversal order. */
  readonly halfEdges: readonly number[]
  /** `from` node of each half-edge, same order. May repeat at cut vertices. */
  readonly ring: readonly NodeId[]
  readonly signedArea: number
  /** Unbounded face: negative signed area under the consistent winding above. */
  readonly isOuter: boolean
  /** Zero area: a tree component, or a face made only of spurs. */
  readonly isDegenerate: boolean
}

/**
 * Every face orbit of the embedding, including the outer face(s) and the
 * degenerate orbits of dangling filaments. The orbits PARTITION the half-edge
 * set: every half-edge appears in exactly one orbit exactly once.
 */
export function traceFaceOrbits(graph: WallGraph): FaceOrbit[] {
  const visited = new Array<boolean>(graph.halfEdges.length).fill(false)
  const faces: FaceOrbit[] = []
  for (let start = 0; start < graph.halfEdges.length; start++) {
    if (visited[start]) continue
    const orbit: number[] = []
    let h = start
    // Bounded by the half-edge count: the successor function is a permutation.
    for (let guard = 0; guard <= graph.halfEdges.length; guard++) {
      visited[h] = true
      orbit.push(h)
      const nxt = nextHalfEdge(graph, h)
      if (nxt === start) break
      h = nxt
    }
    const ring = orbit.map(i => graph.halfEdges[i]!.from)
    const area = signedArea(ringPoints(graph, ring))
    faces.push({
      halfEdges: orbit,
      ring,
      signedArea: area,
      isOuter: area < -AREA_EPS_MM2,
      isDegenerate: Math.abs(area) <= AREA_EPS_MM2,
    })
  }
  return faces
}

/** World points for a ring of canonical node ids. */
export function ringPoints(graph: WallGraph, ring: readonly NodeId[]): Vec2[] {
  const out: Vec2[] = []
  for (const id of ring) {
    const n = graph.nodes.get(id)
    if (n) out.push(n.p)
  }
  return out
}

// ---------------------------------------------------------------------------
// Face extraction into rooms
// ---------------------------------------------------------------------------

export interface RoomFace {
  /** Outer boundary, counter-clockwise, first vertex not repeated last. */
  readonly ring: readonly NodeId[]
  /** Half-edge index for each ring edge ring[i] -> ring[i+1]. */
  readonly ringEdges: readonly number[]
  /** Enclosed rings, clockwise. Rooms nested inside this one, and voids. */
  readonly holes: readonly (readonly NodeId[])[]
  readonly holeEdges: readonly (readonly number[])[]
  /** Shoelace area of `ring`, always positive. */
  readonly outerArea: number
  /** `outerArea` minus the areas of the holes. Never negative. */
  readonly netArea: number
}

export interface OrbitCycle {
  readonly halfEdges: readonly number[]
  readonly ring: readonly NodeId[]
  /** Positive: an outer boundary. Negative: a hole. */
  readonly area: number
}

interface Cycle {
  halfEdges: number[]
  ring: NodeId[]
  area: number
}

/**
 * Strip bridge half-edges out of a face orbit and split what remains into
 * closed cycles.
 *
 * A bridge is traversed TWICE inside the same orbit, out and back, so removing
 * both traversals splits the closed walk into closed sub-walks. The stack here
 * reassembles them regardless of where the cyclic orbit was linearised: when
 * the next kept half-edge does not continue from the current chain, the chain
 * is parked and a new one begun; when a chain closes on itself it is emitted
 * and the parked chain resumes.
 */
function decomposeOrbit(graph: WallGraph, orbit: FaceOrbit): Cycle[] {
  const kept = orbit.halfEdges.filter(i => !graph.bridges.has(graph.halfEdges[i]!.wallId))
  if (kept.length === 0) return []

  const cycles: Cycle[] = []
  const parked: number[][] = []
  let current: number[] = []

  const emit = (chain: number[]): void => {
    const ring = chain.map(i => graph.halfEdges[i]!.from)
    cycles.push({ halfEdges: chain, ring, area: signedArea(ringPoints(graph, ring)) })
  }

  for (const h of kept) {
    const he = graph.halfEdges[h]!
    const last = current[current.length - 1]
    if (last !== undefined && graph.halfEdges[last]!.to !== he.from) {
      parked.push(current)
      current = []
    }
    current.push(h)
    for (;;) {
      const first = current[0]
      const tail = current[current.length - 1]
      if (first === undefined || tail === undefined) break
      if (graph.halfEdges[tail]!.to !== graph.halfEdges[first]!.from) break
      emit(current)
      current = parked.pop() ?? []
      if (current.length === 0) break
    }
  }
  // Anything still open never closed; a well-formed orbit leaves nothing here.
  return cycles
}

/**
 * Public view of {@link decomposeOrbit}: the closed cycles a face orbit breaks
 * into once its bridge half-edges are removed. Exposed because it is the step
 * where "room boundary", "hole" and "outer face" are actually decided, and it
 * deserves to be testable on its own.
 */
export function splitOrbitIntoCycles(graph: WallGraph, orbit: FaceOrbit): OrbitCycle[] {
  return decomposeOrbit(graph, orbit)
}

/**
 * Room faces: the bounded faces of the plan, with bridge walls removed from
 * their boundaries and nested rings attached as holes.
 *
 * The outer face(s) are discarded — they are exactly the orbits that yield no
 * positively-wound cycle. Nesting is resolved twice over: rings enclosed by the
 * same face (a room reached through a corridor bridge) fall straight out of the
 * orbit decomposition, and rings in a *disconnected* component sitting inside
 * another room are matched afterwards by containment testing.
 */
export function extractFaces(graph: WallGraph): RoomFace[] {
  interface Draft {
    ring: NodeId[]
    ringEdges: number[]
    holes: NodeId[][]
    holeEdges: number[][]
    outerArea: number
    points: Vec2[]
  }
  const drafts: Draft[] = []

  for (const orbit of traceFaceOrbits(graph)) {
    const cycles = decomposeOrbit(graph, orbit)
    const positives = cycles.filter(c => c.area > AREA_EPS_MM2)
    const negatives = cycles.filter(c => c.area < -AREA_EPS_MM2)
    if (positives.length === 0) continue // outer face, or a pure filament face

    for (const pos of positives) {
      const points = ringPoints(graph, pos.ring)
      const holes: NodeId[][] = []
      const holeEdges: number[][] = []
      for (const neg of negatives) {
        if (positives.length > 1) {
          const rp = representativeInteriorPoint(ringPoints(graph, neg.ring))
          if (classifyPointInPolygon(rp, points) !== 'inside') continue
        }
        holes.push(neg.ring)
        holeEdges.push(neg.halfEdges)
      }
      drafts.push({
        ring: pos.ring, ringEdges: pos.halfEdges, holes, holeEdges,
        outerArea: pos.area, points,
      })
    }
  }

  // Cross-component nesting: a room drawn inside another room, unconnected to
  // it, never shows up in the containing face's orbit at all.
  for (let i = 0; i < drafts.length; i++) {
    const inner = drafts[i]!
    let bestIdx = -1
    let bestArea = Infinity
    const probe = representativeInteriorPoint(inner.points)
    for (let j = 0; j < drafts.length; j++) {
      if (i === j) continue
      const outer = drafts[j]!
      if (!(outer.outerArea > inner.outerArea + AREA_EPS_MM2)) continue
      if (classifyPointInPolygon(probe, outer.points) !== 'inside') continue
      if (outer.outerArea < bestArea) {
        bestArea = outer.outerArea
        bestIdx = j
      }
    }
    if (bestIdx < 0) continue
    const host = drafts[bestIdx]!
    const reversedRing = [inner.ring[0]!, ...inner.ring.slice(1).reverse()]
    const key = canonicalRingString(reversedRing)
    if (host.holes.some(h => canonicalRingString(h) === key)) continue
    host.holes.push(reversedRing)
    host.holeEdges.push(
      inner.ringEdges.map(e => graph.halfEdges[e]!.twin).reverse(),
    )
  }

  const faces: RoomFace[] = drafts.map(d => {
    // Keep holes in a deterministic order regardless of discovery order.
    const order = d.holes.map((h, idx) => ({ key: canonicalRingString(h), idx }))
    order.sort((a, b) => cmpStr(a.key, b.key))
    const holes = order.map(o => d.holes[o.idx]!)
    const holeEdges = order.map(o => d.holeEdges[o.idx]!)
    let net = d.outerArea
    for (const h of holes) net -= Math.abs(signedArea(ringPoints(graph, h)))
    return {
      ring: d.ring,
      ringEdges: d.ringEdges,
      holes,
      holeEdges,
      outerArea: d.outerArea,
      netArea: Math.max(0, net),
    }
  })

  faces.sort((a, b) => cmpStr(canonicalRingString(a.ring), canonicalRingString(b.ring)))
  return faces
}

/**
 * A rotation-invariant string for a node ring. Two devices that discovered the
 * same room starting from different half-edges produce the same key, so room
 * identity is stable across machines and across edits elsewhere in the plan.
 */
export function canonicalRingString(ring: readonly NodeId[]): string {
  const n = ring.length
  if (n === 0) return ''
  let min = ring[0]!
  for (const id of ring) if (id < min) min = id
  let best: string | null = null
  for (let i = 0; i < n; i++) {
    if (ring[i] !== min) continue
    let s = ''
    for (let k = 0; k < n; k++) s += (k ? '|' : '') + ring[(i + k) % n]!
    if (best === null || s < best) best = s
  }
  return best ?? ''
}

function fnv1a32(s: string, seed: number): number {
  let h = seed >>> 0
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 0x01000193) >>> 0
  }
  return h >>> 0
}

/**
 * Stable derived id for a room. Rooms are not authored, so they cannot carry a
 * uuidv7; their identity has to be a pure function of their boundary or a room
 * label would jump to a different room on every reload.
 */
export function roomKeyOf(ring: readonly NodeId[]): RoomId {
  const s = canonicalRingString(ring)
  const a = fnv1a32(s, 0x811c9dc5)
  const b = fnv1a32(s, 0x01000193)
  return asId<RoomId>(`room-${a.toString(16).padStart(8, '0')}${b.toString(16).padStart(8, '0')}`)
}

// ---------------------------------------------------------------------------
// Rooms
// ---------------------------------------------------------------------------

export interface DetectRoomsOptions extends BuildGraphOptions {
  /** Rooms whose net inner area is below this (mm^2) are dropped. Default 0. */
  minAreaMm2?: number
  /**
   * Force a single half-thickness (mm) for the inward offset instead of using
   * each boundary wall's own thickness. 0 disables offsetting entirely, which
   * makes `polygon` the raw centreline ring.
   */
  halfThicknessMm?: number
}

/**
 * The whole pipeline: plan -> graph -> faces -> rooms with inner polygons.
 *
 * `areaMm2` is the NET INNER area — the offset outer polygon minus the offset
 * holes — because that is the number a human means by "how big is this room".
 * It is clamped at zero for degenerate plans whose walls are thicker than the
 * gaps between them.
 *
 * `centroid` is an ANCHOR, not the area centroid: it is guaranteed to lie
 * strictly inside `polygon` and strictly outside every hole, because it is what
 * a renderer drops a room label, an occupancy tint or a sticky-note pin on. The
 * area centroid is the wrong answer for two entirely ordinary office shapes —
 * it sits in the missing corner of an L-shaped room and in the void of a
 * ring-shaped one — so `interiorPointOf` uses it only when it qualifies.
 */
export function detectRooms(plan: FloorPlan, opts?: DetectRoomsOptions): DetectedRoom[] {
  const graph = buildGraph(plan, opts)
  const faces = extractFaces(graph)
  const minArea = opts?.minAreaMm2 ?? 0
  const forced = opts?.halfThicknessMm

  const halfFor = (edges: readonly number[]): number[] =>
    edges.map(e => {
      if (forced !== undefined) return forced
      const he = graph.halfEdges[e]
      if (!he) return 0
      return (graph.walls.get(he.wallId)?.thickness ?? 0) / 2
    })

  const rooms: DetectedRoom[] = []
  for (const face of faces) {
    const outerPts = ringPoints(graph, face.ring)
    const polygon = offsetRingInwardVariable(outerPts, halfFor(face.ringEdges))
    const holes: Vec2[][] = []
    for (let i = 0; i < face.holes.length; i++) {
      const hp = ringPoints(graph, face.holes[i]!)
      holes.push(offsetRingInwardVariable(hp, halfFor(face.holeEdges[i] ?? [])))
    }
    const outerA = Math.abs(signedArea(polygon))
    let net = outerA
    for (const h of holes) net -= Math.abs(signedArea(h))
    net = Math.max(0, net)
    if (net < minArea) continue

    rooms.push({
      id: roomKeyOf(face.ring),
      ring: [...face.ring],
      polygon,
      areaMm2: net,
      centroid: interiorPointOf(polygon, holes),
      holes,
    })
  }
  return rooms
}

// ---------------------------------------------------------------------------
// Topology edits. Every one returns a NEW plan and never mutates its input.
// When an edit does not apply, the input plan is returned unchanged (identical
// reference), so callers can test applicability with ===.
// ---------------------------------------------------------------------------

/**
 * The 1-D interval an opening occupies in wall-local coordinates.
 *
 * SPEC RESOLUTION: `Opening.offset` is the distance from node A to the opening's
 * START, so the opening covers [offset, offset + width]. docs/02 §6 describes
 * openings as "cut from the wall by 1-D interval subtraction in wall-local
 * coordinates", and an interval is naturally a start plus a length. Every
 * offset computation in this module goes through this one function, so the
 * other reading (offset = centre) is a one-line change.
 */
export function openingInterval(o: Opening): { start: number; end: number } {
  const w = Math.max(0, o.width)
  return { start: o.offset, end: o.offset + w }
}

/** Sum of every wall's centreline length, in mm. */
export function totalWallLength(plan: FloorPlan): number {
  const pos = new Map<NodeId, Vec2>()
  for (const n of plan.nodes) if (!pos.has(n.id)) pos.set(n.id, n.p)
  let total = 0
  for (const w of plan.walls) {
    const a = pos.get(w.a)
    const b = pos.get(w.b)
    if (a && b) total += len(sub(b, a))
  }
  return total
}

/**
 * Re-host an opening onto a different wall.
 *
 * `dA`/`dB` are where the opening's current host wall's own node A and node B
 * sit along the NEW wall, measured from the new wall's node A. That single
 * mapping covers every case — split children keep their direction, merged
 * children may be reversed.
 *
 * CLAMPING, NOT DROPPING: when the new wall is too short to hold the whole
 * opening the interval is clamped to [0, newLength]. Only if less than
 * MIN_OPENING_WIDTH_MM survives is the opening dropped — a door that has been
 * squeezed to nothing is not a door, but a door that lost 30 mm still is, and
 * silently deleting the user's doors is far worse than shortening one.
 *
 * The width is carried across as a WIDTH rather than reconstructed from two
 * translated endpoints, and is only recomputed when the clamp actually bit. On
 * a diagonal wall `dA` is irrational, so `(dA + end) - (dA + start)` drifts by
 * an ulp or two — enough to make "the split preserved my 900 mm door" false by
 * 6e-14, which is exactly the kind of drift that accumulates over an editing
 * session.
 */
function rehostOpening(
  o: Opening,
  wallId: WallId,
  dA: number,
  dB: number,
  newLength: number,
): Opening | null {
  const iv = openingInterval(o)
  const width = Math.max(0, o.width)
  const start = dB >= dA ? dA + iv.start : dA - iv.end
  const end = start + width
  const cs = Math.max(0, start)
  const ce = Math.min(newLength, end)
  if (ce - cs < MIN_OPENING_WIDTH_MM) return null
  const clamped = cs !== start || ce !== end
  return { ...o, wallId, offset: mm(cs), width: mm(clamped ? ce - cs : width) }
}

export interface SplitWallIds {
  /** Fresh id for the node created at the split point. */
  node: NodeId
  /** Fresh id for the child running from the original node A to the split. */
  wallA: WallId
  /** Fresh id for the child running from the split to the original node B. */
  wallB: WallId
}

export interface SplitWallOptions {
  /**
   * What to do when the split point lands strictly inside a door or window.
   *
   * `false` (the default) REFUSES the split and returns the plan unchanged, so
   * `splitWall(...) === plan` tells the caller nothing happened. That is the
   * only setting under which the operation is lossless: an opening cannot be
   * cut in two without minting an id, and this module has no id source.
   *
   * `true` restores the older behaviour — the opening goes to the child holding
   * the larger part of it and is CLAMPED to fit, which shortens the user's door
   * and moves one of its ends. Opt in only when the caller has already decided
   * that losing part of the opening is acceptable.
   */
  clampStraddlingOpenings?: boolean
}

/**
 * Split a wall in two at `atPoint`.
 *
 * `atPoint` is PROJECTED onto the wall centreline and the new node is placed at
 * the projection, so splitting never moves the wall. Splitting at (or within
 * COINCIDENT_EPS_MM of) either endpoint is a no-op: it would create a
 * zero-length wall, which `buildGraph` would immediately drop again.
 *
 * Openings are reassigned to the child that contains them, with offsets
 * rebased; an opening that ends exactly at the split point is not straddling
 * and moves whole. An opening the split would cut THROUGH makes the whole
 * operation a no-op by default, because the alternatives all lose data: one
 * child would have to keep a clamped — that is, shortened and shifted — copy,
 * and `mergeCollinearWalls` cannot put back the millimetres that went missing,
 * so the documented split/merge round trip would stop being a round trip. Pass
 * `{ clampStraddlingOpenings: true }` to take the clamp anyway.
 *
 * Document metadata (`updatedAt`, `version`) is deliberately untouched: this
 * module has no clock, and stamping the document is the store's job.
 */
export function splitWall(
  plan: FloorPlan,
  wallId: WallId,
  atPoint: Vec2,
  ids: SplitWallIds,
  opts?: SplitWallOptions,
): FloorPlan {
  if (ids.wallA === ids.wallB) return plan
  const wall = plan.walls.find(w => w.id === wallId)
  if (!wall) return plan
  const na = plan.nodes.find(n => n.id === wall.a)
  const nb = plan.nodes.find(n => n.id === wall.b)
  if (!na || !nb) return plan
  if (plan.nodes.some(n => n.id === ids.node)) return plan
  if (plan.walls.some(w => w.id === ids.wallA || w.id === ids.wallB)) return plan

  const ab = sub(nb.p, na.p)
  const l2 = lenSq(ab)
  const L = Math.sqrt(l2)
  if (L <= COINCIDENT_EPS_MM) return plan
  const t = dot(sub(atPoint, na.p), ab) / l2
  const s = t * L
  if (s <= COINCIDENT_EPS_MM || s >= L - COINCIDENT_EPS_MM) return plan

  if (opts?.clampStraddlingOpenings !== true) {
    for (const o of plan.openings) {
      if (o.wallId !== wallId) continue
      const iv = openingInterval(o)
      if (iv.start < s - COINCIDENT_EPS_MM && iv.end > s + COINCIDENT_EPS_MM) return plan
    }
  }

  const p = add(na.p, scale(ab, t))
  const childA: Wall = { ...wall, id: ids.wallA, a: wall.a, b: ids.node }
  const childB: Wall = { ...wall, id: ids.wallB, a: ids.node, b: wall.b }

  const nodes: PlanNode[] = [...plan.nodes, { id: ids.node, p }]
  const walls: Wall[] = []
  for (const w of plan.walls) {
    if (w.id === wallId) {
      walls.push(childA, childB)
    } else {
      walls.push(w)
    }
  }

  const openings: Opening[] = []
  for (const o of plan.openings) {
    if (o.wallId !== wallId) {
      openings.push(o)
      continue
    }
    const iv = openingInterval(o)
    // Where the original wall's own node A and node B sit along each child.
    const ontoA = (): Opening | null => rehostOpening(o, ids.wallA, 0, L, s)
    const ontoB = (): Opening | null => rehostOpening(o, ids.wallB, -s, L - s, L - s)
    let target: Opening | null
    if (iv.end <= s) {
      target = ontoA()
    } else if (iv.start >= s) {
      target = ontoB()
    } else {
      // Straddles the split. Keep the larger part.
      target = s - iv.start >= iv.end - s ? ontoA() : ontoB()
    }
    if (target) openings.push(target)
  }

  return { ...plan, nodes, walls, openings }
}

export interface MergeCollinearOptions {
  /**
   * How far from straight two walls may be and still merge, in radians.
   * Default 1e-9 — effectively exact, because a floor plan editor snaps.
   */
  angleToleranceRad?: number
  /** Require identical thickness, layer and material to merge. Default true. */
  requireSameProperties?: boolean
}

interface JoinPlan {
  node: PlanNode
  survivor: Wall
  absorbed: Wall
  /** Merged wall endpoints. */
  a: NodeId
  b: NodeId
  /** Distance along the merged wall of the survivor's own a and b. */
  survivorDA: number
  survivorDB: number
  absorbedDA: number
  absorbedDB: number
  length: number
}

/**
 * Work out how two walls meeting at `node` fold into one, or null if they
 * cannot (wrong degree, shared far endpoint, an existing wall already joining
 * the far endpoints, or a zero-length arm).
 */
function planJoin(
  plan: FloorPlan,
  nodeId: NodeId,
  posOf: ReadonlyMap<NodeId, Vec2>,
  incident: readonly Wall[],
): JoinPlan | null {
  if (incident.length !== 2) return null
  const sorted = [...incident].sort((x, y) => cmpStr(x.id, y.id))
  const w1 = sorted[0]!
  const w2 = sorted[1]!
  const far1 = w1.a === nodeId ? w1.b : w1.a
  const far2 = w2.a === nodeId ? w2.b : w2.a
  if (far1 === nodeId || far2 === nodeId) return null // self-loop arm
  if (far1 === far2) return null // would collapse to a 2-gon
  for (const w of plan.walls) {
    if (w.id === w1.id || w.id === w2.id) continue
    if ((w.a === far1 && w.b === far2) || (w.a === far2 && w.b === far1)) return null
  }
  const pN = posOf.get(nodeId)
  const p1 = posOf.get(far1)
  const p2 = posOf.get(far2)
  const node = plan.nodes.find(n => n.id === nodeId)
  if (!pN || !p1 || !p2 || !node) return null
  const L1 = len(sub(p1, pN))
  const L2 = len(sub(p2, pN))
  if (L1 <= COINCIDENT_EPS_MM || L2 <= COINCIDENT_EPS_MM) return null
  // Distinct ids at the same position: the merged wall would have zero length
  // and `buildGraph` would drop it on the next pass, taking its openings with
  // it. Refuse, so the caller's destructive path runs instead of a silent loss.
  if (len(sub(p2, p1)) <= COINCIDENT_EPS_MM) return null

  // Orient the merged wall so the survivor's own direction is preserved.
  let a: NodeId
  let b: NodeId
  let survivorDA: number
  let survivorDB: number
  if (w1.b === nodeId) {
    // survivor runs far1 -> node, so merged runs far1 -> far2.
    a = far1
    b = far2
    survivorDA = 0
    survivorDB = L1
  } else {
    // survivor runs node -> far1, so merged runs far2 -> far1.
    a = far2
    b = far1
    survivorDA = L2
    survivorDB = L2 + L1
  }
  const dOf = (id: NodeId): number => {
    if (id === a) return 0
    if (id === b) return L1 + L2
    return a === far1 ? L1 : L2
  }
  return {
    node,
    survivor: w1,
    absorbed: w2,
    a,
    b,
    survivorDA,
    survivorDB,
    absorbedDA: dOf(w2.a),
    absorbedDB: dOf(w2.b),
    length: L1 + L2,
  }
}

function applyJoin(plan: FloorPlan, join: JoinPlan): FloorPlan {
  const merged: Wall = { ...join.survivor, a: join.a, b: join.b }
  const walls: Wall[] = []
  for (const w of plan.walls) {
    if (w.id === join.absorbed.id) continue
    walls.push(w.id === join.survivor.id ? merged : w)
  }
  const nodes = plan.nodes.filter(n => n.id !== join.node.id)
  const openings: Opening[] = []
  for (const o of plan.openings) {
    if (o.wallId === join.survivor.id) {
      const r = rehostOpening(o, merged.id, join.survivorDA, join.survivorDB, join.length)
      if (r) openings.push(r)
    } else if (o.wallId === join.absorbed.id) {
      const r = rehostOpening(o, merged.id, join.absorbedDA, join.absorbedDB, join.length)
      if (r) openings.push(r)
    } else {
      openings.push(o)
    }
  }
  return { ...plan, nodes, walls, openings }
}

function incidenceOf(plan: FloorPlan): Map<NodeId, Wall[]> {
  const map = new Map<NodeId, Wall[]>()
  for (const n of plan.nodes) map.set(n.id, [])
  for (const w of plan.walls) {
    map.get(w.a)?.push(w)
    if (w.b !== w.a) map.get(w.b)?.push(w)
  }
  return map
}

function positionsOf(plan: FloorPlan): Map<NodeId, Vec2> {
  const pos = new Map<NodeId, Vec2>()
  for (const n of plan.nodes) if (!pos.has(n.id)) pos.set(n.id, n.p)
  return pos
}

/**
 * Dissolve every degree-2 node whose two walls are collinear and otherwise
 * identical, folding each pair into one wall and re-hosting their openings.
 *
 * Runs to a fixed point (a chain of five collinear segments becomes one wall),
 * always choosing the lexicographically smaller wall id as the survivor and
 * always visiting nodes in sorted id order, so the result does not depend on
 * array order.
 */
export function mergeCollinearWalls(plan: FloorPlan, opts?: MergeCollinearOptions): FloorPlan {
  const tol = opts?.angleToleranceRad ?? 1e-9
  const sameProps = opts?.requireSameProperties ?? true
  let current = plan
  // Each successful merge removes one wall, so this cannot spin.
  for (let guard = 0; guard <= plan.walls.length; guard++) {
    const pos = positionsOf(current)
    const inc = incidenceOf(current)
    const nodeIds = [...inc.keys()].sort(cmpStr)
    let applied = false
    for (const nodeId of nodeIds) {
      const incident = inc.get(nodeId)!
      if (incident.length !== 2) continue
      const w1 = incident[0]!
      const w2 = incident[1]!
      if (sameProps && (w1.thickness !== w2.thickness || w1.layer !== w2.layer ||
        w1.material !== w2.material)) continue
      const far1 = w1.a === nodeId ? w1.b : w1.a
      const far2 = w2.a === nodeId ? w2.b : w2.a
      const pN = pos.get(nodeId)
      const p1 = pos.get(far1)
      const p2 = pos.get(far2)
      if (!pN || !p1 || !p2) continue
      const d1 = normalize(sub(p1, pN))
      const d2 = normalize(sub(p2, pN))
      // Collinear AND opposite: the node is a straight-through joint. Two arms
      // leaving in the SAME direction are overlapping walls, not a joint.
      if (Math.abs(cross(d1, d2)) > Math.sin(tol)) continue
      if (dot(d1, d2) >= 0) continue
      const join = planJoin(current, nodeId, pos, incident)
      if (!join) continue
      current = applyJoin(current, join)
      applied = true
      break
    }
    if (!applied) break
  }
  return current
}

export interface RemoveNodeOptions {
  /**
   * When the node has exactly two incident walls, fold them into one instead
   * of deleting them — a "dissolve" rather than a "delete". Collinearity is
   * NOT required; the surviving wall simply runs between the two far nodes.
   * Default false.
   *
   * CALLER BEWARE: because collinearity is not required, the merged wall is a
   * straight line between two points that were previously joined by a corner,
   * and it can therefore cross other walls. This module cannot fix that — a
   * crossing with no node at the crossing is not a planar wall graph, and every
   * face is computed from the planar embedding. Dissolving a genuine corner is
   * an editor gesture that has to be re-validated by whatever offers it.
   */
  heal?: boolean
}

/**
 * Remove a node.
 *
 * Default (destructive): the node goes, every wall touching it goes, and every
 * opening hosted on those walls goes with them. Neighbouring nodes are left in
 * place even if they end up isolated — they are authored data, and an editor
 * that silently deletes points the user placed is infuriating.
 *
 * With `heal: true` and exactly two incident walls, the two walls are joined
 * into one and their openings are re-hosted onto it. If healing is impossible
 * (wrong degree, the far nodes coincide, or a wall already joins them) the
 * destructive path is taken instead.
 */
export function removeNode(
  plan: FloorPlan,
  nodeId: NodeId,
  opts?: RemoveNodeOptions,
): FloorPlan {
  if (!plan.nodes.some(n => n.id === nodeId)) return plan
  if (opts?.heal === true) {
    const inc = incidenceOf(plan).get(nodeId) ?? []
    const join = planJoin(plan, nodeId, positionsOf(plan), inc)
    if (join) return applyJoin(plan, join)
  }
  const doomed = new Set<WallId>()
  for (const w of plan.walls) if (w.a === nodeId || w.b === nodeId) doomed.add(w.id)
  return {
    ...plan,
    nodes: plan.nodes.filter(n => n.id !== nodeId),
    walls: plan.walls.filter(w => !doomed.has(w.id)),
    openings: plan.openings.filter(o => !doomed.has(o.wallId)),
  }
}

/** World-space endpoints of an opening, for verifying it did not move. */
export function openingWorldSpan(
  plan: FloorPlan,
  opening: Opening,
): { start: Vec2; end: Vec2 } | null {
  const wall = plan.walls.find(w => w.id === opening.wallId)
  if (!wall) return null
  const pos = positionsOf(plan)
  const a = pos.get(wall.a)
  const b = pos.get(wall.b)
  if (!a || !b) return null
  const ab = sub(b, a)
  const L = len(ab)
  if (L <= 0) return null
  const d = scale(ab, 1 / L)
  const iv = openingInterval(opening)
  return {
    start: add(a, scale(d, iv.start)),
    end: add(a, scale(d, iv.end)),
  }
}

/** Convenience: `Mm`-typed length of one wall. */
export function wallLength(plan: FloorPlan, wallId: WallId): Mm {
  const w = plan.walls.find(x => x.id === wallId)
  if (!w) return mm(0)
  const pos = positionsOf(plan)
  const a = pos.get(w.a)
  const b = pos.get(w.b)
  if (!a || !b) return mm(0)
  return mm(len(sub(b, a)))
}
